import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  const [status, setStatus] = useState('loading');
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= 5) {
      setStatus('pending');
      return;
    }

    try {
      const result = await api.getCheckoutStatus(sessionId);
      
      if (result.payment_status === 'paid') {
        setStatus('success');
      } else if (result.status === 'expired') {
        setStatus('expired');
      } else {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#C9B896] mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-2xl font-semibold mb-2">
              Vérification du paiement...
            </h1>
            <p className="text-muted-foreground">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-heading text-3xl font-semibold mb-2" data-testid="payment-success">
              Paiement réussi !
            </h1>
            <p className="text-muted-foreground mb-8">
              Merci pour votre achat ! Vous pouvez maintenant accéder à votre formation.
            </p>
            <Link to={L('/mes-formations')}>
              <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA] font-semibold px-8">
                Accéder à mes formations
              </Button>
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="font-heading text-3xl font-semibold mb-2">
              Paiement en cours de traitement
            </h1>
            <p className="text-muted-foreground mb-8">
              Votre paiement est en cours de validation. Vous recevrez un email de confirmation.
            </p>
            <Link to={L('/mes-formations')}>
              <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA] font-semibold px-8">
                Voir mes formations
              </Button>
            </Link>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-heading text-3xl font-semibold mb-2" data-testid="payment-error">
              {status === 'expired' ? 'Session expirée' : 'Erreur de paiement'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {status === 'expired' 
                ? 'La session de paiement a expiré. Veuillez réessayer.'
                : 'Une erreur est survenue lors du paiement. Veuillez réessayer.'}
            </p>
            <Link to={L('/formations-en-ligne')}>
              <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA] font-semibold px-8">
                Retour aux formations
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancelPage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  return (
    <div className="min-h-screen pt-24 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 bg-navy-light rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-muted-foreground" />
        </div>
        <h1 className="font-heading text-3xl font-semibold mb-2" data-testid="payment-cancelled">
          Paiement annulé
        </h1>
        <p className="text-muted-foreground mb-8">
          Vous avez annulé le paiement. Aucun montant n'a été débité.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={L('/formations-en-ligne')}>
            <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA] font-semibold px-8">
              Retour aux formations
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
